import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { Grid } from '../../engine/data/common/Grid';
import { ChangeTracker } from '../changes/ChangeTracker';
import { DoseSnapshot, TrackableObject } from '../changes/DomainChange';
import { DoseId, newDoseId } from '../changes/ids';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { DomainValidationError } from '../errors/DomainError';
import { OptimizationObjective } from './optimizationObjective';
import {
    DicomDose,
    DoseSummationType,
    DoseType,
    DoseUnit,
} from '../dicom/dicomDose';
import { Dvh } from './dvh';
import { Plan } from './plan';
import { StructureSet } from './structureset';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { calculateDvh } from '~/engine/util/algorithms/sdf/DVHCalculation';
import { DoseTargetData } from '~/utils/transforms/doseTargetsTransforms';

/**
 * Deep copy of the optimization-objective graph. The types are plain data
 * (numbers, strings, booleans) with a single nested `subRows` array — no
 * class instances, functions or cycles — so an explicit two-level copy is
 * both sufficient and cheaper than a structured clone.
 */
function copyObjectives(
    objectives: OptimizationObjective[] | undefined
): OptimizationObjective[] | undefined {
    return objectives?.map((objective) => ({
        ...objective,
        subRows: objective.subRows.map((subRow) => ({ ...subRow })),
    }));
}

/**
 * {@link DoseMatrix} is a container for dose information.
 *
 * Display settings (doseScale, normalizationValueGy, selectedRange) are NOT
 * held here — they are visualization parameters and live in the UI layer's
 * per-dose display store, keyed by {@link DoseId}. What remains on the
 * instance beyond the DICOM-backed clinical fields is the attached
 * prescription metadata (targets, optimizationObjectives): untracked, but
 * set once at ingest and enforced as such by its setters.
 */
export class DoseMatrix implements TrackableObject {
    readonly trackableKind = 'dose' as const;

    /**
     * Unique runtime identifier generated when the {@link DoseMatrix} is
     * constructed. Accepts an existing id so a persisted dose can be
     * round-tripped without minting a new identity.
     */
    public readonly id: DoseId;

    private tracker: ChangeTracker | null = null;

    /**
     * Create a {@link DoseMatrix} from DICOM object
     * @param dose DICOM dose object
     * @returns DoseMatrix
     */
    static fromDose(dose: DicomDose): DoseMatrix {
        const res = vec3.fromValues(
            dose.pixelSpacing[0],
            dose.pixelSpacing[1],
            dose.sliceResolution
        );
        const size = vec3.fromValues(dose.cols, dose.rows, dose.frames);
        return new DoseMatrix(
            dose,
            Grid.fromDicom(
                size,
                res,
                dose.imagePosition,
                dose.imageOrientation
            ),
            dose.doseUnits,
            dose.doseType,
            dose.doseSummationType,
            dose.doseGridScaling,
            dose.referencedRtPlanUid
        );
    }

    public constructor(
        private readonly _dicomDose: DicomDose,
        grid: Grid,
        doseUnits: DoseUnit,
        doseType: DoseType,
        doseSummationType: DoseSummationType,
        doseGridScaling: number,
        private readonly _referencedRtPlanUid: string,
        id: DoseId = newDoseId()
    ) {
        this.grid = grid;
        this.doseUnits = doseUnits;
        this.doseType = doseType;
        this.doseSummationType = doseSummationType;
        this.doseGridScaling = doseGridScaling;
        this.id = id;
    }

    get trackableId(): DoseId {
        return this.id;
    }

    attachTracker(tracker: ChangeTracker): void {
        this.tracker = tracker;
    }

    captureSnapshot(): DoseSnapshot {
        // Only the dose's clinical identity, links and export state are
        // tracked. Display settings (scale, normalization, colorwash range)
        // are UI state and live outside the aggregate; prescription
        // targets/objectives are plain attached metadata set once at ingest,
        // not tracked mutations — so none of them appear here.
        return {
            id: this.id,
            sopInstanceUid: this.sopInstanceUid,
            doseType: this.doseType,
            doseSummationType: this.doseSummationType,
            planId: this._plan?.id ?? null,
            structureSetId: this.structureSet?.id ?? null,
            isNew: this.isNew,
            isExported: this.isExported,
        };
    }

    public get sopInstanceUid(): string {
        return this._dicomDose.sopInstanceUid;
    }

    public get generationModel(): GenerationModelDetails | undefined {
        return this._dicomDose.generationModel;
    }

    public get isNew(): boolean {
        return this._dicomDose.isNew;
    }

    public get isExported(): boolean {
        return this._dicomDose.isExported;
    }

    public setExported(): void {
        // Mark BEFORE mutating. markChanged throws outside a command(), and
        // flipping the DICOM object first would leave it permanently
        // exported with no changeset emitted. Snapshots are captured at
        // flush, after the mutation, so this ordering does not affect what
        // the changeset reports.
        this.tracker?.markChanged(this);
        this._dicomDose.setExported();
    }

    public get plan(): Plan | null {
        return this._plan;
    }

    /**
     * Links (or unlinks, with `null`) the plan this dose was computed for.
     * Does not emit a changeset — link wiring happens at ingest, outside
     * any command() context; the Patient cascade marks the dose instead.
     * Same convention as {@link Plan.linkDoseMatrix}.
     */
    public linkPlan(plan: Plan | null): void {
        this._plan = plan;
    }

    public get structureSet(): StructureSet | null {
        if (this._plan) {
            return this._plan.structureSet;
        }
        return this._structureSet;
    }

    /**
     * Links (or unlinks, with `null`) the structure set this dose is
     * evaluated against. When a plan is linked, the plan owns the
     * structure-set reference and this must agree with it. Does not emit
     * a changeset — see {@link linkPlan}.
     *
     * @throws {DomainValidationError} if a plan is linked and its
     *   structure set differs from the one supplied.
     */
    public linkStructureSet(structureSet: StructureSet | null): void {
        if (
            structureSet !== null &&
            this._plan &&
            this._plan.structureSet !== structureSet
        ) {
            throw new DomainValidationError(
                'Cannot set structure set when plan has different structure set'
            );
        }
        // Unlinking always clears the direct reference, even when a plan is
        // linked. A dose can hold both (RTDOSE+RTSTRUCT imported first, the
        // RTPLAN later), and the getter then reads only the plan's link — so
        // a plan-linked dose could otherwise retain a reference to a
        // structure set already removed from the aggregate.
        if (!this._plan || structureSet === null) {
            this._structureSet = structureSet;
        }
    }

    /**
     * Drops the dose's own reference to `structureSet` if it holds one.
     * Returns whether anything was actually detached, so the caller knows
     * whether to mark a change.
     *
     * The {@link structureSet} getter delegates to the plan when one is
     * linked, so a `dose.structureSet === ss` test cannot see a direct
     * reference that is being shadowed by a plan link. This inspects the
     * stored field instead, which is what keeps a removed structure set
     * from being retained by a plan-linked dose.
     */
    public detachStructureSet(structureSet: StructureSet): boolean {
        if (this._structureSet === structureSet) {
            this._structureSet = null;
            return true;
        }
        return false;
    }

    public get dicomDose(): DicomDose {
        return this._dicomDose;
    }

    public referencesPlanUid(uid: string): boolean {
        return uid === this._referencedRtPlanUid;
    }

    private _plan: Plan | null = null;

    // direct structure set reference, null if we have a plan reference instead
    private _structureSet: StructureSet | null = null;

    private _optimizationObjectives?: OptimizationObjective[];

    /**
     * Returns a deep copy — see {@link setOptimizationObjectives}.
     */
    public get optimizationObjectives(): OptimizationObjective[] | undefined {
        return copyObjectives(this._optimizationObjectives);
    }

    /**
     * Stores a deep copy of the objectives.
     *
     * Prescription objectives are plain attached metadata, set once from the
     * Dose+ prediction — not a tracked clinical mutation, so this is NOT
     * routed through the change tracker. It still deep-copies on write (and
     * the getter on read) so a later UI-side edit cannot reach into the
     * stored graph; the nested `subRows` array is copied too.
     *
     * Set-once is enforced rather than merely documented: with no tracker
     * there is no changeset, so a later write would leave every consumer of
     * the dose projection (`useDoseDetailView` and the target lookups built
     * on it) showing the previous prescription indefinitely, with nothing to
     * signal the divergence. Fail loudly at the write instead.
     */
    public setOptimizationObjectives(
        objectives: OptimizationObjective[] | undefined
    ): void {
        if (this._optimizationObjectives !== undefined) {
            throw new DomainValidationError(
                'Optimization objectives are set once at ingest and cannot ' +
                    'be replaced on a dose that already has them.'
            );
        }
        this._optimizationObjectives = copyObjectives(objectives);
    }

    // ── Attached prescription metadata (not stored in DICOM, not tracked) ──

    private _targets?: DoseTargetData[];

    public readonly grid: Grid;

    public readonly doseUnits: DoseUnit;

    public get dicomUid(): string {
        return this.sopInstanceUid;
    }

    public readonly doseType: DoseType;

    public readonly doseSummationType: DoseSummationType;

    private readonly doseGridScaling: number;

    public get pixelData(): Uint32Array {
        return this._dicomDose.pixelData;
    }

    /**
     * The dose in Gy for every voxel.
     *
     * Delegates rather than scaling again: the underlying object already
     * holds these values, and keeping a second copy here meant a 45 MB dose
     * carried two identical Float32 arrays — and built this one by walking an
     * iterator into a new array and then mapping that into another, which
     * took about 420 ms against about 8 ms for the indexed loop it now reuses.
     */
    public get realPixels(): Float32Array {
        return this._dicomDose.realPixels;
    }

    /**
     * True dose in Gy for one stored voxel value (DICOM dose grid scaling),
     * without forcing the whole-volume {@link realPixels} Float32 copy.
     */
    public toDoseGy(storedValue: number): number {
        return storedValue * this.doseGridScaling;
    }

    // Cached argmax of `realPixels`: both the max dose value (Gy) and the
    // patient-space (mm) coordinates of the winning voxel center, computed in
    // a single pass. `position` is null only for an empty dose grid.
    //
    // Note: the display-time dose scale is a uniform multiplier applied in the
    // UI layer and so does NOT affect argmax — this cache is stable for the
    // life of the instance. `doseMaxGy` here is the unscaled maximum.
    private _doseMaxCache?: {
        valueGy: number;
        position: ReadonlyVec3 | null;
    };

    private getDoseMaxCache(): {
        valueGy: number;
        position: ReadonlyVec3 | null;
    } {
        if (this._doseMaxCache) {
            return this._doseMaxCache;
        }
        const data = this.realPixels;
        if (data.length === 0) {
            this._doseMaxCache = { valueGy: 0, position: null };
            return this._doseMaxCache;
        }
        let maxVal = -Infinity;
        let maxIdx = 0;
        for (let i = 0; i < data.length; i++) {
            if (data[i] > maxVal) {
                maxVal = data[i];
                maxIdx = i;
            }
        }
        this._doseMaxCache = {
            valueGy: maxVal,
            position: this.grid.indexToVoxelCenterWorldSpace(maxIdx),
        };
        return this._doseMaxCache;
    }

    public get doseMaxGy(): number {
        return this.getDoseMaxCache().valueGy;
    }

    /**
     * Patient-space (mm) coordinates of the voxel center holding the maximum
     * dose value. Returns null for an empty dose grid.
     *
     * On ties (multiple voxels with the same maximum value) the first voxel
     * encountered in raster order is returned, matching
     * {@link Image3D.findMaxIntensityPosition} semantics.
     */
    public get doseMaxPosition(): ReadonlyVec3 | null {
        return this.getDoseMaxCache().position;
    }

    /**
     * Prediction targets associated with this dose (prescription per
     * structure). Attached metadata, not stored in DICOM and not tracked.
     * Returns a copy — see {@link setTargets}.
     */
    public get targets(): DoseTargetData[] | undefined {
        return this._targets?.map((target) => ({ ...target }));
    }

    /**
     * Stores a copy of the targets. Set once from the Dose+ prediction, not
     * a tracked mutation. Copies on write (and the getter on read) so a
     * later UI-side edit cannot reach into the stored array.
     *
     * Set-once is enforced for the same reason as
     * {@link setOptimizationObjectives}: an untracked second write has no
     * invalidation path to the dose projection, so it would silently leave
     * the prescription readouts showing the superseded targets.
     */
    public setTargets(value: DoseTargetData[] | undefined): void {
        if (this._targets !== undefined) {
            throw new DomainValidationError(
                'Dose targets are set once at ingest and cannot be replaced ' +
                    'on a dose that already has them.'
            );
        }
        this._targets = value?.map((target) => ({ ...target }));
    }

    /**
     * Cached calculated DVHs keyed by ROI reference.
     * Each cache entry stores the DVH plus the ROI SDF/contour version used to compute it.
     */
    private _calculatedDvhs: Map<
        RegionOfInterest,
        { version: number; dvh: Dvh }
    > = new Map();

    /**
     * Resolve the version number of the ROI's SDF/contours.
     * Supports new sdfVersion or legacy contourVersion (fallback).
     */
    private _getRoiVersion(roi: RegionOfInterest): number {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyRoi = roi as any;
        return (
            (typeof anyRoi.sdfVersion === 'number' && anyRoi.sdfVersion) ||
            (typeof anyRoi.contourVersion === 'number' &&
                anyRoi.contourVersion) ||
            0
        );
    }
    /**
     * Get (and cache) a calculated DVH for the ROI.
     * Recomputes automatically if ROI version has changed since last calculation.
     */
    public async getCalculatedDvh(roi: RegionOfInterest): Promise<Dvh> {
        const currentVersion = this._getRoiVersion(roi);
        const cached = this._calculatedDvhs.get(roi);

        if (cached && cached.version === currentVersion) {
            return cached.dvh;
        }

        const dvhRes = await calculateDvh(this, roi);
        const dvh = new Dvh(
            dvhRes.cumDvhRelative,
            dvhRes.binWidthGy,
            dvhRes.doseMaxGy,
            dvhRes.doseMinGy,
            dvhRes.doseMeanGy,
            dvhRes.structureVolume_mm3 / 1000
        );

        this._calculatedDvhs.set(roi, {
            version: currentVersion,
            dvh,
        });

        return dvh;
    }

    /**
     * Invalidate a single ROI DVH cache entry (e.g., after external forced ROI update).
     */
    public clearCalculatedDvh(roi: RegionOfInterest): void {
        this._calculatedDvhs.delete(roi);
    }

    /**
     * Clear all cached calculated DVHs.
     */
    public clearAllCalculatedDvhs(): void {
        this._calculatedDvhs.clear();
    }
}
